import moment from 'moment';
import _ from 'lodash';
import { DateTimeFormats } from 'brock-date-time';
import {
    shouldShowFilter,
    getFilterOptions,
    filterHasValue,
} from './filtersModalUtils';

/* eslint-disable complexity */
export function timeRangeRollup(
    timeAndPrecision,
    dashboardLabels,
    showTimeAndPrecision = true
) {
    if (!showTimeAndPrecision) {
        return null;
    }

    if (
        timeAndPrecision.timeAndPrecisionType === 'last' ||
        timeAndPrecision.timeAndPrecisionType === 'next'
    ) {
        return `${dashboardLabels[timeAndPrecision.timeAndPrecisionType]} ${
            timeAndPrecision.rangeValue
        } ${dashboardLabels[timeAndPrecision.rangeType]}`;
    }

    if (timeAndPrecision.timeAndPrecisionType === 'predefined') {
        return dashboardLabels[timeAndPrecision.predefinedDateRange];
    }

    if (timeAndPrecision.timeAndPrecisionType === 'fixedDay') {
        return moment(timeAndPrecision.startDateTime).format(
            DateTimeFormats.DATE
        );
    }

    return `${moment(timeAndPrecision.startDateTime).format(
        DateTimeFormats.DATETIME
    )} ${dashboardLabels.to} ${moment(timeAndPrecision.endDateTime).format(
        DateTimeFormats.DATETIME
    )}`;
}
/* eslint-enable complexity */

function precisionRollup(
    timeAndPrecision,
    dashboardLabels,
    showPrecisionConfig = true
) {
    if (!showPrecisionConfig) {
        return null;
    }

    return `${dashboardLabels.every} ${timeAndPrecision.precisionValue} ${
        dashboardLabels[timeAndPrecision.precisionType]
    }`;
}

export function otherFiltersRollup(
    dashboardFilterValues,
    dashboardWhereClauses,
    availableFilters,
    requiredFilterKeys,
    showMeasureFilters,
    selectOptions,
    dashboardLabels
) {
    const numberOfFilters = _(availableFilters)
        .filter((availableFilter, filterKey) => {
            const options = getFilterOptions(
                availableFilters,
                filterKey,
                dashboardFilterValues,
                selectOptions,
                requiredFilterKeys
            );

            return (
                filterHasValue(dashboardFilterValues[filterKey]) &&
                shouldShowFilter(
                    availableFilter,
                    options,
                    requiredFilterKeys.includes(filterKey)
                )
            );
        })
        .values()
        .value().length;

    const numberOfMeasures = showMeasureFilters
        ? dashboardWhereClauses.length
        : 0;

    const totalFiltersApplied = numberOfFilters + numberOfMeasures;

    if (!totalFiltersApplied) {
        return null;
    }

    return totalFiltersApplied === 1
        ? `1 ${dashboardLabels.filter}`
        : `${totalFiltersApplied} ${dashboardLabels.filters}`;
}

/* eslint-disable complexity */
export function mergeRollupTexts(
    timeRollupString,
    precisionRollupString,
    otherFiltersRollupString
) {
    let rollupText = '';

    if (timeRollupString) {
        rollupText = timeRollupString;
    }

    if (precisionRollupString) {
        rollupText = rollupText
            ? `${rollupText}, ${precisionRollupString}`
            : precisionRollupString;
    }

    if (otherFiltersRollupString) {
        rollupText = rollupText
            ? `${rollupText}, +${otherFiltersRollupString}`
            : otherFiltersRollupString;
    }

    return rollupText;
}
/* eslint-enable complexity */

export default function buildRollupText(
    timeAndPrecision,
    dashboardFilterValues,
    dashboardWhereClauses,
    showTimeAndPrecision,
    showPrecisionConfig,
    showMeasureFilters,
    availableFilters,
    requiredFilterKeys,
    selectOptions,
    dashboardLabels
) {
    const timeRollupString = timeRangeRollup(
        timeAndPrecision,
        dashboardLabels,
        showTimeAndPrecision
    );

    const precisionRollupString = precisionRollup(
        timeAndPrecision,
        dashboardLabels,
        showTimeAndPrecision && showPrecisionConfig
    );

    const otherFiltersRollupString = otherFiltersRollup(
        dashboardFilterValues,
        dashboardWhereClauses,
        availableFilters,
        requiredFilterKeys,
        showMeasureFilters,
        selectOptions,
        dashboardLabels
    );

    return mergeRollupTexts(
        timeRollupString,
        precisionRollupString,
        otherFiltersRollupString
    );
}

export function buildCustomDashboardRollupText(
    timeAndPrecision,
    dashboardLabels
) {
    const timeRollupString = timeRangeRollup(timeAndPrecision, dashboardLabels);

    const precisionRollupString = precisionRollup(
        timeAndPrecision,
        dashboardLabels
    );

    return mergeRollupTexts(timeRollupString, precisionRollupString);
}
